/**
 * Ordering + triage for the preflight report (#1421 P3b).
 *
 * The report is large by design — up to 500 coverage rows and 500 target
 * health rows — and an operator reading it mid-incident has one question:
 * *what would break if I switched right now?* So the panel never renders the
 * report in wire order. Every section here drops the rows that carry no
 * finding, sorts what remains worst-first, and reports how many rows it left
 * out rather than silently trimming.
 */
import type {
  RoutingConfigPreflightResponse,
  RoutingPreflightSkuCoverage,
  RoutingPreflightSkuDiff,
  RoutingPreflightTargetHealth,
  RoutingPreflightUnreachableOrg,
} from '@nadoutong/contracts';

/**
 * Rows rendered before a section collapses behind "show all".
 *
 * Chosen so the three sections above the fold plus the first screen of the
 * biggest one fit without the operator scrolling past a finding. The cut is
 * always on a worst-first sort, so what survives is the part that matters;
 * "show all" is a real control, not a placeholder.
 */
export const REPORT_SECTION_PREVIEW_ROWS = 25;

/** `uncovered` is a hard gap; `fall_through` still serves traffic. */
const COVERAGE_SEVERITY: Record<RoutingPreflightSkuCoverage['status'], number> = {
  uncovered: 0,
  fall_through: 1,
  covered: 2,
};

/**
 * Coverage rows worth reading: everything the candidate does not itself cover,
 * hard gaps first, then by traffic. A `covered` row is the expected case and
 * is reported as a count instead — 400 rows of "fine" is how the two rows that
 * are not fine get missed.
 */
export function coverageFindings(
  coverage: readonly RoutingPreflightSkuCoverage[],
): readonly RoutingPreflightSkuCoverage[] {
  return coverage
    .filter((row) => row.status !== 'covered')
    .slice()
    .sort((a, b) => {
      const bySeverity = COVERAGE_SEVERITY[a.status] - COVERAGE_SEVERITY[b.status];
      if (bySeverity !== 0) return bySeverity;
      if (a.requests !== b.requests) return b.requests - a.requests;
      return a.sku_id.localeCompare(b.sku_id);
    });
}

/**
 * Target health rows worth reading: blocking findings first (the selector
 * would never attempt those), then degradations (it still would), then by how
 * many SKUs ride on the target.
 */
export function healthFindings(
  health: readonly RoutingPreflightTargetHealth[],
): readonly RoutingPreflightTargetHealth[] {
  return health
    .filter((row) => row.findings.length > 0)
    .slice()
    .sort((a, b) => {
      if (a.healthy !== b.healthy) return a.healthy ? 1 : -1;
      if (a.sku_ids.length !== b.sku_ids.length) return b.sku_ids.length - a.sku_ids.length;
      return a.upstream_id.localeCompare(b.upstream_id);
    });
}

/** Orgs a global switch would not reach, busiest first. */
export function unreachableOrgsByTraffic(
  orgs: readonly RoutingPreflightUnreachableOrg[],
): readonly RoutingPreflightUnreachableOrg[] {
  return orgs
    .slice()
    .sort((a, b) =>
      a.requests !== b.requests ? b.requests - a.requests : a.org_id.localeCompare(b.org_id),
    );
}

/** The per-SKU before/after diff already contains only changed rows. */
export function skuDiffByTraffic(
  diff: readonly RoutingPreflightSkuDiff[],
): readonly RoutingPreflightSkuDiff[] {
  return diff
    .slice()
    .sort((a, b) =>
      a.requests !== b.requests ? b.requests - a.requests : a.sku_id.localeCompare(b.sku_id),
    );
}

/**
 * The one-line verdict at the top of the report: what would break, ranked. The
 * order is the order an operator has to act on them — a hard gap is a
 * tenant-visible 502, an unreachable org is a switch that silently does
 * nothing for that customer, and a fall-through still serves.
 */
export type PreflightHeadline = 'uncovered' | 'unhealthy' | 'unreachable' | 'changed' | 'clean';

export function preflightHeadline(report: RoutingConfigPreflightResponse): PreflightHeadline {
  const { summary } = report;
  if (summary.uncovered > 0) return 'uncovered';
  if (summary.unhealthy_targets > 0) return 'unhealthy';
  if (summary.unreachable_orgs_total > 0) return 'unreachable';
  if (summary.changed_skus > 0 || summary.fall_through > 0 || summary.degraded_targets > 0) {
    return 'changed';
  }
  return 'clean';
}
